import { typeKey } from "../finance/presets";

// The roadmap's phase vocabulary and Thread's standard onboarding flow.
// Spec: docs/superpowers/specs/2026-10-29-standard-onboarding-flow-design.md
//
// A step may carry, beyond { id, label, description, status, date }:
//   owner:    "thread" | "client" | "both"  (shown to the client as Thread / You / Together)
//   internal: true                          (Thread only: never on the client page)
// Both are optional; stored steps without them render as before.
export const PHASE_ORDER = ["Pre-engagement", "Activation", "In Flight"];
export const OWNERS = ["thread", "client", "both"];

// One standard flow per engagement type. `week` is weeks from the start date
// (week 0 is kickoff). Pre-engagement is history by the time an engagement
// row exists, so it is seeded undated for Thread to tick. Monthly coaching is
// not a step: the ledger and the statement record it month by month.
// Ids are `std-…` so they never collide with older stored ids (ac1, if3…).
const PRE = [
  { key: "discovery", label: "Discovery conversation", description: "We listen: where the team is, why now, and what a good year looks like", owner: "both" },
  { key: "proposal", label: "Proposal", description: "We shape the programme, people and investment with you", owner: "thread" },
  { key: "sign-off", label: "Sign-off", description: "You confirm in writing. We take it from here", owner: "client" },
  { key: "agreement", label: "Agreement", description: "Services agreement drafted by us, signed by both", owner: "both" },
  { key: "first-invoice", label: "First invoice", description: "Issued ahead of the start date and paid before kickoff", owner: "client" },
  { key: "client-brief", label: "Client brief written", description: "The business, the people, why they engaged, language to avoid", owner: "thread", internal: true },
  { key: "hub-setup", label: "Hub set up", description: "Engagement created, Finance Config declared from the agreement, link shared", owner: "thread", internal: true },
  { key: "drive-folders", label: "Drive folders created", description: "Client folder with People and Pulse subfolders", owner: "thread", internal: true },
];

export const STANDARD_FLOW = {
  programme: {
    "Pre-engagement": PRE,
    "Activation": [
      { key: "kickoff", week: 0, label: "Kickoff", description: "One hour with your lead. We agree the sequence, the dates and anything sensitive, and lock the off-site date", owner: "both" },
      { key: "roster", week: 0, label: "Team roster confirmed", description: "Names, roles and emails, and anyone to leave out. You send it, we check it", owner: "client" },
      { key: "people-folders", week: 0, label: "People folders created", description: "One Drive folder per person on the roster", owner: "thread", internal: true },
      { key: "introduce", week: 1, label: "You introduce Thread to the team", description: "A short note in your own channel. We draft it, you send it", owner: "client" },
      { key: "hello", week: 1, label: "Personal hello from Thread", description: "An email to each person with a link to book a quick chat", owner: "thread" },
      { key: "intro-calls", week: 2, label: "Intro calls", description: "Fifteen minutes with each person. Just turn up", owner: "thread" },
      { key: "founder", week: 2, label: "Founder or CEO 1:1", description: "A strategic conversation. The key business points come back to your lead", owner: "both" },
      { key: "coaches", week: 2, label: "Coaches matched", description: "We pair each person being coached with their Thread coach", owner: "thread" },
      { key: "first-coaching", week: 3, label: "First coaching sessions", description: "An opening session with each person. We set the recurring rhythm in the diary", owner: "thread" },
      { key: "pulse-roster", week: 3, label: "Pulse roster saved", description: "Confirmed roster saved to the Pulse folder, re-checked before the send", owner: "thread", internal: true },
      { key: "baseline-pulse", week: 4, label: "Baseline Pulse", description: "First measurement. We send the survey and the reminders; your team gives it ten minutes", owner: "thread" },
      { key: "pulse-readout", week: 6, label: "Pulse readout to leadership", description: "The results go to the leadership team first, in private", owner: "thread" },
      { key: "share-back", week: 7, label: "Team share-back", description: "Themes shared with the whole team. Grouped, never individual", owner: "both" },
      { key: "offsite", week: 9, label: "Leadership off-site", description: "We design and run it, you host it. The Strategic Anchor comes out of it", owner: "both" },
    ],
    "In Flight": [
      { key: "anchor-shared", week: 10, label: "Strategic Anchor shared with the company", description: "You present it. We prepare the material", owner: "both" },
      { key: "quarterly-1", week: 13, label: "Quarterly strategic check-in", description: "Leadership get-together. We facilitate", owner: "both" },
      { key: "pulse-2", week: 21, label: "Pulse 2", description: "Roster refreshed, second measurement and report", owner: "thread" },
      { key: "quarterly-2", week: 26, label: "Quarterly strategic check-in", description: "Leadership get-together. We facilitate", owner: "both" },
      { key: "pulse-3", week: 35, label: "Pulse 3", description: "Third measurement and report", owner: "thread" },
      { key: "quarterly-3", week: 39, label: "Quarterly strategic check-in", description: "Leadership get-together. We facilitate", owner: "both" },
      { key: "review", week: 48, label: "Year-one review", description: "The year in review, and what comes next", owner: "both" },
    ],
  },
  coaching: {
    "Pre-engagement": PRE,
    "Activation": [
      { key: "kickoff", week: 0, label: "Kickoff with your sponsor", description: "We agree who is being coached, what good looks like and how we report back", owner: "both" },
      { key: "coachees", week: 0, label: "Coaching clients confirmed", description: "Names and emails of the people being coached. You send them, we take it from there", owner: "client" },
      { key: "people-folders", week: 0, label: "People folders created", description: "One Drive folder per person being coached", owner: "thread", internal: true },
      { key: "coaches", week: 1, label: "Coach matched", description: "We pair each person with their Thread coach", owner: "thread" },
      { key: "first-coaching", week: 2, label: "First sessions", description: "An opening session with each person. Just turn up", owner: "thread" },
      { key: "rhythm", week: 3, label: "Recurring sessions in the diary", description: "We set the rhythm with each person's calendar", owner: "thread" },
    ],
    "In Flight": [
      { key: "midpoint", week: 12, label: "Midpoint check-in with your sponsor", description: "How it is going, in themes, never individual detail", owner: "both" },
      { key: "block-review", week: 24, label: "Block review", description: "What has changed, and whether a next block makes sense", owner: "both" },
    ],
  },
};

const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// The first Monday of a "YYYY-MM" month: the start date when only the month is known.
export function firstMonday(ym) {
  const d = new Date(`${ym}-01T00:00:00Z`);
  const shift = (8 - d.getUTCDay()) % 7; // 0 when the 1st is a Monday
  return addDays(`${ym}-01`, shift);
}

// The standard flow for a type, as stored steps, dated from `start`
// ("YYYY-MM-DD"). Without a start date every step is undated.
export function standardSteps(type, start) {
  const flow = STANDARD_FLOW[typeKey(type)] || STANDARD_FLOW.programme;
  const out = {};
  PHASE_ORDER.forEach(phase => {
    out[phase] = flow[phase].map(({ key, week, ...rest }) => {
      const s = { id: `std-${key}`, ...rest, status: "to-start", date: start && week !== undefined ? addDays(start, week * 7) : "" };
      return s;
    });
  });
  return out;
}

export function sortByDate(items) {
  return [...items].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.localeCompare(b.date);
  });
}
